import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icon,
  cn,
} from "@nexus/ui";
import { useTranslation } from "react-i18next";

import { PERSONA_EXTRA_ROLLING_PRESET } from "@/features/opportunities/components/sets/CreateSetDateRangePicker/CreateSetDateRangePicker.js";
import { SetDateWindowMeta } from "@/features/opportunities/components/sets/SetDateWindowMeta/SetDateWindowMeta.js";
import { humanizeBrandName } from "@/features/opportunities/lib/format-opportunity.js";
import { useRelativeTime } from "@/features/opportunities/lib/format-relative-time.js";
import { isPendingPersonaSetId } from "../../../lib/persona-opportunity-set-generation-state.js";
import { personaOpportunitySetDisplayTimestamp } from "../../../lib/persona-opportunity-set-mapper.js";
import type { PersonaOpportunitySet } from "../../../types/persona-opportunities.types.js";

export interface PersonaOpportunitySetCardProps {
  set: PersonaOpportunitySet;
  onClick?: () => void;
  /** Cancel a generating set — only passed when the caller may create/generate. */
  onStop?: (set: PersonaOpportunitySet) => void;
  /** Soft-delete READY/FAILED sets — omitted while GENERATING (Stop handles cancel). */
  onDelete?: (set: PersonaOpportunitySet) => void;
  /** Re-run generation for FAILED sets. */
  onRetry?: (set: PersonaOpportunitySet) => void;
  /** Open regenerate dialog for READY sets. */
  onRegenerate?: (set: PersonaOpportunitySet) => void;
  /** GENERATE permission gate for mutating menu items. */
  canMutate?: boolean;
}

function TagChip({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-[11px] text-foreground/80">
      {label}
    </span>
  );
}

function Stat({
  value,
  label,
  accentClassName,
}: {
  value: number;
  label: string;
  accentClassName?: string;
}) {
  return (
    <span className="inline-flex items-baseline gap-1.5">
      <span className={cn("text-base font-semibold tabular-nums", accentClassName ?? "text-foreground")}>
        {value}
      </span>
      <span className="text-[11px] text-muted-foreground">{label}</span>
    </span>
  );
}

export function PersonaOpportunitySetCard({
  set,
  onClick,
  onStop,
  onDelete,
  onRetry,
  onRegenerate,
  canMutate = true,
}: PersonaOpportunitySetCardProps) {
  const { t } = useTranslation();
  const relativeDate = useRelativeTime(personaOpportunitySetDisplayTimestamp(set));
  const isGenerating = set.status === "GENERATING";
  const isFailed = set.status === "FAILED";
  const isReady = set.status === "READY";
  const isAwaitingSetId = isGenerating && isPendingPersonaSetId(set.id);
  // FAILED stays non-interactive for open. GENERATING cards open once the real set id lands.
  const isInteractive = !!onClick && !isFailed && (!isGenerating || !isAwaitingSetId);
  const showDelete = !!onDelete && (isReady || isFailed);
  const showRetry = !!onRetry && isFailed;
  const showRegenerate = !!onRegenerate && isReady;
  const showMenu = (showDelete || showRetry || showRegenerate) && canMutate;

  const tags = [
    set.industry ? humanizeBrandName(set.industry) : null,
    set.adPlatform ? humanizeBrandName(set.adPlatform) : null,
  ].filter((tag): tag is string => !!tag);

  return (
    <div
      role={isInteractive ? "button" : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      aria-busy={isGenerating || undefined}
      aria-label={
        isInteractive
          ? t("personaOpportunities.setCard.openSetAria", {
              name: set.name,
              defaultValue: `Open set ${set.name}`,
            })
          : undefined
      }
      onClick={isInteractive ? onClick : undefined}
      onKeyDown={(event) => {
        if (!isInteractive) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick?.();
        }
      }}
      className={cn(
        "group/card relative flex min-h-[210px] w-full flex-col overflow-hidden rounded-md border border-border bg-card text-left transition-shadow",
        isInteractive && "cursor-pointer hover:shadow-md",
        isGenerating && "border-primary/20",
        isFailed && "border-destructive/30 bg-destructive/[0.03]",
      )}
    >
      <div className="flex flex-1 flex-col px-5 pb-4 pt-5">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-semibold leading-snug text-foreground">{set.name}</div>
            <div className="mt-1 text-[11px] text-muted-foreground">{relativeDate}</div>
            <SetDateWindowMeta
              startDate={set.startDate}
              endDate={set.endDate}
              extraRollingPresets={[PERSONA_EXTRA_ROLLING_PRESET]}
              className="mt-1.5"
            />
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {showMenu ? (
              <DropdownMenu>
                <DropdownMenuTrigger
                  asChild
                  onClick={(event) => event.stopPropagation()}
                >
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    className="shrink-0 text-muted-foreground min-h-11 min-w-11 [@media(hover:hover)_and_(pointer:fine)]:min-h-7 [@media(hover:hover)_and_(pointer:fine)]:min-w-7"
                    aria-label={t("personaOpportunities.setCard.actionsMenuAria", "Set actions")}
                  >
                    <Icon name="more-horizontal" size="sm" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" onClick={(event) => event.stopPropagation()}>
                  {showRegenerate ? (
                    <DropdownMenuItem
                      className="cursor-pointer"
                      onClick={() => onRegenerate?.(set)}
                    >
                      {t("personaOpportunities.workspace.regenerate", "Regenerate")}
                    </DropdownMenuItem>
                  ) : null}
                  {showRetry ? (
                    <DropdownMenuItem
                      className="cursor-pointer"
                      onClick={() => onRetry?.(set)}
                    >
                      {t("common.retry", "Retry")}
                    </DropdownMenuItem>
                  ) : null}
                  {(showRegenerate || showRetry) && showDelete ? <DropdownMenuSeparator /> : null}
                  {showDelete ? (
                    <DropdownMenuItem
                      className="cursor-pointer text-destructive focus:text-destructive"
                      onClick={() => onDelete?.(set)}
                    >
                      {t("common.remove", "Remove")}
                    </DropdownMenuItem>
                  ) : null}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
            {isInteractive && (
              <Icon
                name="chevron-right"
                size="sm"
                className="mt-0.5 shrink-0 text-muted-foreground/50 transition-colors group-hover/card:text-muted-foreground"
                aria-hidden
              />
            )}
          </div>
        </div>

        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {tags.map((tag) => (
              <TagChip key={tag} label={tag} />
            ))}
          </div>
        )}

        {isFailed ? (
          <div className={cn("space-y-1.5", tags.length > 0 && "mt-3")}>
            <p className="text-xs font-medium leading-relaxed text-destructive">
              {t("personaOpportunities.setCard.generationFailed", "Generation failed")}
            </p>
            <p className="text-xs leading-relaxed text-muted-foreground">
              {t(
                "personaOpportunities.setCard.generationFailedHint",
                "Use Retry from the menu, create a new set, or contact support if it keeps failing.",
              )}
            </p>
            {showRetry && canMutate ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-2 w-fit"
                onClick={(event) => {
                  event.stopPropagation();
                  onRetry?.(set);
                }}
              >
                {t("common.retry", "Retry")}
              </Button>
            ) : null}
          </div>
        ) : isGenerating ? (
          <div className={cn("mt-auto flex flex-col gap-3", tags.length > 0 && "mt-3")}>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Icon name="loader" size="xs" className="animate-spin text-primary" aria-hidden />
              {t("personaOpportunities.setCard.generating", "Generating…")}
            </div>
            {onStop && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-fit"
                onClick={(event) => {
                  event.stopPropagation();
                  onStop(set);
                }}
              >
                {t("personaOpportunities.setCard.stop", "Stop")}
              </Button>
            )}
          </div>
        ) : null}
      </div>

      {!isFailed && (
        <div className="flex items-center gap-4 border-t border-border/60 bg-muted/40 px-5 py-3">
          <Stat
            value={isGenerating ? 0 : set.opportunityCount}
            label={t("personaOpportunities.setCard.opportunitiesLabel", "opportunities")}
            accentClassName="text-foreground"
          />
          <Stat
            value={isGenerating ? 0 : set.scriptCount}
            label={t("personaOpportunities.setCard.scriptsLabel", "scripts")}
            accentClassName="text-primary"
          />
          <Stat
            value={0}
            label={t("personaOpportunities.setCard.sentLabel", "sent to vendor")}
            accentClassName="text-status-success"
          />
        </div>
      )}
    </div>
  );
}
